import { Building2, Scale, UserRoundCog, Wallet } from "lucide-react";
import type { IAdvisor, IAdvisorMeta, TAdvisorKind } from "./types";

/**
 * Front-end only for now. When the API lands this list comes from
 * `GET /ai/expertise` (per-tenant, permission-filtered) and `locked` carries
 * whatever the admin turned off — the shape below is the contract to hit.
 *
 * JSX-free by design (see CLAUDE.md): icons are component references, and
 * every derived label goes through `advisorMeta`.
 *
 * `name`, `tagline`, `helper` and `placeholder` hold `common:ai_assistant.*`
 * LOOKUP KEYS, not display text — a module-scope array cannot call a hook, so
 * consumers render them through `t()`. `slug` stays a literal: it is the API
 * contract value.
 */
export const ADVISOR_DEFAULT_SLUG = "company-only";

export const ADVISORS: IAdvisor[] = [
  {
    slug: ADVISOR_DEFAULT_SLUG,
    name: "common:ai_assistant.company_only",
    icon: Building2,
    tagline: "common:ai_assistant.company_only_tagline",
    helper: "common:ai_assistant.company_only_helper",
    kind: "free",
    webEnabled: false,
    domainCount: 0,
    creditsPerSearch: 0,
    placeholder: "common:ai_assistant.company_only_placeholder",
    personal: {
      name: "common:ai_assistant.company_only_personal_name",
      helper: "common:ai_assistant.company_only_personal_helper",
    },
  },
  {
    slug: "uae-labour-law",
    name: "common:ai_assistant.uae_labour_law",
    icon: Scale,
    tagline: "common:ai_assistant.uae_labour_law_tagline",
    helper: "common:ai_assistant.uae_labour_law_helper",
    kind: "statute",
    webEnabled: true,
    domainCount: 6,
    creditsPerSearch: 3,
    placeholder: "common:ai_assistant.uae_labour_law_placeholder",
  },
  {
    slug: "hr-advisor",
    name: "common:ai_assistant.hr_advisor",
    icon: UserRoundCog,
    tagline: "common:ai_assistant.hr_advisor_tagline",
    helper: "common:ai_assistant.hr_advisor_helper",
    kind: "practice",
    webEnabled: true,
    domainCount: 9,
    creditsPerSearch: 2,
    placeholder: "common:ai_assistant.hr_advisor_placeholder",
    personal: {
      tagline: "common:ai_assistant.hr_advisor_personal_tagline",
      helper: "common:ai_assistant.hr_advisor_personal_helper",
    },
  },
  {
    slug: "payroll-tax",
    name: "common:ai_assistant.payroll_tax",
    icon: Wallet,
    tagline: "common:ai_assistant.payroll_tax_tagline",
    helper: "common:ai_assistant.payroll_tax_helper",
    kind: "statute",
    webEnabled: true,
    domainCount: 5,
    creditsPerSearch: 3,
    placeholder: "common:ai_assistant.payroll_tax_placeholder",
    personal: {
      tagline: "common:ai_assistant.payroll_tax_personal_tagline",
      helper: "common:ai_assistant.payroll_tax_personal_helper",
    },
  },
];

/** Advisor kind → Badge variant. The one place this mapping lives. */
export const ADVISOR_KIND_VARIANT: Record<
  TAdvisorKind,
  "success" | "info" | "neutral" | "primary"
> = {
  free: "success",
  statute: "info",
  practice: "neutral",
  regulation: "primary",
};

/** Static list, so the grouping is computed once at module load, not per render. */
export const ADVISOR_GROUPS = {
  internal: ADVISORS.filter((a) => !a.webEnabled && !a.locked),
  web: ADVISORS.filter((a) => a.webEnabled && !a.locked),
  locked: ADVISORS.filter((a) => a.locked),
};

/** Every selectable advisor, in the order the pickers show them. */
export const SELECTABLE_ADVISORS = [
  ...ADVISOR_GROUPS.internal,
  ...ADVISOR_GROUPS.web,
];

export const findAdvisor = (slug: string | null | undefined): IAdvisor =>
  ADVISORS.find((a) => a.slug === slug) ?? ADVISORS[0];

export const isSelectable = (advisor: IAdvisor) => !advisor.locked;

/** Overlays an advisor's `personal` copy when rendering for an employee
 *  ("My Space") rather than the company-wide surface. Advisors with no
 *  `personal` override (UAE Labour Law today) render unchanged either way. */
export const resolveAdvisorCopy = (
  advisor: IAdvisor,
  isPersonal: boolean,
): IAdvisor =>
  isPersonal && advisor.personal
    ? { ...advisor, ...advisor.personal }
    : advisor;

/**
 * Display copy derived from one advisor. Kept here so the dropdown row and the
 * greeting chips cannot drift apart.
 */
export const advisorMeta = (advisor: IAdvisor): IAdvisorMeta =>
  advisor.webEnabled
    ? {
        sources: "common:ai_assistant.approved_sources",
        sourcesCount: advisor.domainCount,
      }
    : { sources: "common:ai_assistant.your_data_only" };
